import React, { useMemo } from "react"
import { Table, Tag, Space, Typography, Result } from "@mimber/ui-web"
import { DollarOutlined, UserOutlined } from "@mimber/ui-web"
import { useOrders } from "@mimber/client/mimber-organization"
import type { ColumnsType, TableProps } from "@mimber/ui-web"
import type { OrderWithRelationsDto } from "@mimber/schema"
import { OrderStatus, OrderSourceType } from "@mimber/schema"
import { formatCurrency } from "@/utils"
import type { OrdersTableProps } from "./OrdersTable.types"
import { SOURCE_LABELS, STATUS_COLORS } from "./OrdersTable.utils"
import {
  handleTableChange,
  applyQueryParamsToColumns,
  getColumnSearchProps,
  formatTableDate,
} from "@mimber/ui-web"
import { useRememberedTableQueryParams } from "@/core/hooks"
import {
  getRememberedPaginationConfig,
  getRememberedTableLocale,
} from "@/utils"

const { Text } = Typography

export const OrdersTable: React.FunctionComponent<OrdersTableProps> = ({
  onRowClick,
  className,
  ...tableProps
}) => {
  const { queryParams, setQueryParams, resetQueryParams } =
    useRememberedTableQueryParams({
      tableId: "orders",
      defaultQueryParams: {
        page: 1,
        limit: 20,
      },
    })

  const { data: response, isLoading, error } = useOrders(queryParams)

  const columns: ColumnsType<OrderWithRelationsDto> = useMemo(
    () => [
      {
        title: "Order #",
        dataIndex: "orderNumber",
        key: "orderNumber",
        sorter: true,
        sortDirections: ["ascend", "descend"] as const,
        ...getColumnSearchProps("Search order number"),
      },
      {
        title: "Source",
        dataIndex: "sourceType",
        key: "sourceType",
        render: (sourceType: OrderSourceType) => (
          <Text>{SOURCE_LABELS[sourceType] ?? sourceType}</Text>
        ),
        filters: Object.values(OrderSourceType).map((type) => ({
          text: SOURCE_LABELS[type] ?? type,
          value: type,
        })),
      },
      {
        title: "Member",
        key: "member",
        render: (_: unknown, record: OrderWithRelationsDto) => {
          const user = record.membership?.user
          if (!user) return <Text type="secondary">—</Text>
          return (
            <Space>
              <UserOutlined />
              <Text>{user.displayName}</Text>
            </Space>
          )
        },
      },
      {
        title: "Amount",
        key: "totalAmount",
        sorter: true,
        sortDirections: ["ascend", "descend"] as const,
        render: (_: unknown, record: OrderWithRelationsDto) => (
          <Space>
            <DollarOutlined />
            <Text>{formatCurrency(record.totalAmount)}</Text>
          </Space>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status: OrderStatus) => (
          <Tag color={STATUS_COLORS[status] ?? "default"}>{status}</Tag>
        ),
        filters: Object.values(OrderStatus).map((status) => ({
          text: status,
          value: status,
        })),
      },
      {
        title: "Created",
        dataIndex: "createdAt",
        key: "createdAt",
        sorter: true,
        sortDirections: ["ascend", "descend"] as const,
        render: (createdAt: string) => (
          <Text type="secondary">{formatTableDate(createdAt)}</Text>
        ),
      },
    ],
    []
  )

  const displayColumns = useMemo(
    () => applyQueryParamsToColumns(columns, queryParams),
    [columns, queryParams]
  )

  const onTableChange: TableProps<OrderWithRelationsDto>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    const tableChangeData = {
      pagination,
      filters: filters as Record<string, string[] | null>,
      sorter: {
        field: Array.isArray(sorter) ? sorter[0]?.field : sorter?.field,
        order: Array.isArray(sorter) ? sorter[0]?.order : sorter?.order,
      } as { field?: string | string[]; order?: "ascend" | "descend" },
    }
    handleTableChange(tableChangeData, setQueryParams, columns)
  }

  const paginationConfig = getRememberedPaginationConfig(
    response?.meta?.pagination?.total || 0,
    queryParams,
    resetQueryParams
  )

  if (error) {
    return (
      <Result
        status="error"
        title="Failed to load orders"
        subTitle={error.message}
      />
    )
  }

  return (
    <>
      <Table<OrderWithRelationsDto>
        bordered
        columns={displayColumns}
        dataSource={response?.data || []}
        loading={isLoading}
        rowKey="uuid"
        pagination={paginationConfig}
        locale={getRememberedTableLocale(resetQueryParams, queryParams)}
        onChange={onTableChange}
        onRow={(record) => ({
          onClick: () => onRowClick?.(record),
          style: { cursor: onRowClick ? "pointer" : "default" },
        })}
        className={className}
        {...tableProps}
      />
    </>
  )
}
